import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';

import { accentButtonSx } from './SummaryBar.styles';
import { Tag } from '../../../@types';
import { AutocompleteTagType, mapSavedTags, Tags } from '../../organisms/Tags';
import { toTagCellItems } from '../TagCell';
import { TagChips } from '../TagChips';

export interface TagsFieldProps {
  tags: Tag[];
  allTags: Tag[];
  /** When omitted the chips render read-only and no edit affordance appears. */
  onSave?: (args: {
    selectedTags: (AutocompleteTagType | string)[];
    newTags: Tag[];
  }) => Promise<void>;
}

/** Tag chips with an inline popover editor, mirroring the legacy job header. */
export const TagsField: React.FC<TagsFieldProps> = ({ tags, allTags, onSave }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedTags, setSelectedTags] = useState<(AutocompleteTagType | string)[]>([]);
  const [newTags, setNewTags] = useState<Tag[]>([]);

  const handleOpen = () => {
    setSelectedTags(mapSavedTags(tags) ?? []);
    setNewTags([]);
    setError('');
    setOpen(true);
  };

  // The Tags editor renders its option list and colour picker in portals, so
  // clicks inside them land outside this listener's subtree and would
  // otherwise discard the in-progress edit.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.MuiAutocomplete-popper, .MuiPopover-root')) {
      return;
    }
    setOpen(false);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ selectedTags, newTags });
      setOpen(false);
    } catch {
      setError('Could not save tags. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      ref={anchorRef}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
      // Escape dismisses the editor whether focus is still on the trigger or
      // has moved into the portalled popover — React events bubble through the
      // portal, so both land here.
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', flexShrink: 1 }}>
        <TagChips tags={toTagCellItems(tags)} onTagClick={onSave ? handleOpen : undefined} />
      </Box>

      {onSave && (
        <IconButton
          size="small"
          aria-label={tags.length > 0 ? 'Edit tags' : 'Add tags'}
          onClick={handleOpen}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            sx={{
              width: 360,
              p: 1.5,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
            }}
          >
            <Tags
              selectedTags={selectedTags}
              newTags={newTags}
              setSeletedTags={setSelectedTags}
              setNewTags={setNewTags}
              allTags={allTags}
            />
            {error && (
              <Typography role="alert" sx={{ mt: 1, fontSize: '12px', color: 'error.main' }}>
                {error}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving}
                sx={accentButtonSx}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
